import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { COUNTRY_OPTIONS, QUALIFICATIONS } from "@/lib/brand";

const empty = { full_name: "", email: "", phone: "", country: "", qualification: "", course: "", message: "" };
const SUCCESS_INITIAL = { opacity: 0, scale: 0.96 };
const SUCCESS_ANIMATE = { opacity: 1, scale: 1 };

export default function EnquiryForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.email || !form.phone || !form.country) {
      setError("Please fill in your name, email, phone and preferred country.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      setForm(empty);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-slate-50 border border-transparent focus:border-navy focus:ring-2 focus:ring-navy/10 px-4 py-3.5 text-navy placeholder:text-slate-400 outline-none transition-all";

  if (done) {
    return (
      <motion.div
        initial={SUCCESS_INITIAL}
        animate={SUCCESS_ANIMATE}
        data-testid="enquiry-success"
        className="bg-white rounded-3xl p-10 text-center shadow-xl border border-gold/20"
      >
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h3 className="font-display text-2xl font-bold text-navy mb-2">Thank you!</h3>
        <p className="text-slate-600 mb-6">
          Your enquiry has been received. Our counsellor will contact you shortly. For faster response, WhatsApp us your details.
        </p>
        <button
          data-testid="enquiry-reset"
          onClick={() => setDone(false)}
          className="rounded-full bg-navy hover:bg-navy-light text-white px-6 py-3 font-semibold transition-colors"
        >
          Submit another enquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="bg-white rounded-3xl p-7 md:p-9 shadow-xl border border-gold/20 space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-semibold text-navy mb-1.5 block">Full Name *</label>
          <input data-testid="enquiry-name" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label className="text-sm font-semibold text-navy mb-1.5 block">Phone Number *</label>
          <input data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold text-navy mb-1.5 block">Email Address *</label>
        <input data-testid="enquiry-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-semibold text-navy mb-1.5 block">Preferred Country *</label>
          <select data-testid="enquiry-country" value={form.country} onChange={set("country")} className={inputCls}>
            <option value="">Select country</option>
            {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold text-navy mb-1.5 block">Qualification</label>
          <select data-testid="enquiry-qualification" value={form.qualification} onChange={set("qualification")} className={inputCls}>
            <option value="">Select qualification</option>
            {QUALIFICATIONS.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold text-navy mb-1.5 block">Course Interested In</label>
        <input data-testid="enquiry-course" value={form.course} onChange={set("course")} placeholder="e.g. MS in Computer Science" className={inputCls} />
      </div>

      <div>
        <label className="text-sm font-semibold text-navy mb-1.5 block">Message</label>
        <textarea data-testid="enquiry-message" rows="3" value={form.message} onChange={set("message")} placeholder="Tell us about your study plans" className={inputCls} />
      </div>

      {error && <p data-testid="enquiry-error" className="text-sm text-red-600 font-medium">{error}</p>}

      <button
        type="submit"
        data-testid="enquiry-submit"
        disabled={loading}
        className="w-full rounded-full bg-navy hover:bg-navy-light text-white py-4 font-bold flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99] transition-all"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {loading ? "Submitting..." : "Submit Enquiry"}
      </button>
      <p className="text-xs text-center text-slate-400">100% free registration · No obligations · We never share your data.</p>
    </form>
  );
}
